import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Button, MenuItem, TextField, Alert, CircularProgress,
  Radio, RadioGroup, FormControlLabel, Chip, Divider,
} from '@mui/material';
import { SaveOutlined, ScheduleOutlined, CheckCircle, WarningAmber } from '@mui/icons-material';
import {
  P, cardSx, fieldSx, priBtn, outBtn, PX, FieldLabel, StatusPill, WorkspaceHeader, ValidationStrip,
} from '../../components/common/workspaceKit';
import { recurringService } from '../../services/recurringService';
import { financeWorkspaceService } from '../../services/financeWorkspaceService';
import { coaService, CoaAccount } from '../../services/coaService';
import { settingsService } from '../../services/settingsService';
import { formatIndianNumber } from '../../utils/currency';

const FREQUENCIES: { value: string; label: string }[] = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'LAST_DAY_MONTHLY', label: 'Monthly (last day)' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'HALF_YEARLY', label: 'Half-yearly' },
  { value: 'YEARLY', label: 'Yearly' },
];
const POSTING_MODES: { value: string; label: string; hint: string }[] = [
  { value: 'AUTO_POST', label: 'Auto-post', hint: 'Generated journal is validated and posted automatically.' },
  { value: 'DRAFT', label: 'Create Draft', hint: 'Journal is generated but left unposted for review.' },
  { value: 'APPROVAL', label: 'Require Approval', hint: 'Generated journal waits for sign-off before posting.' },
];

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Client-side mirror of the backend advanceSchedule() so the upcoming-runs
// preview matches exactly what generation will do.
const advance = (dateStr: string, freq: string): string => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  switch (freq) {
    case 'DAILY': dt.setDate(dt.getDate() + 1); break;
    case 'WEEKLY': dt.setDate(dt.getDate() + 7); break;
    case 'LAST_DAY_MONTHLY': dt.setDate(1); dt.setMonth(dt.getMonth() + 2); dt.setDate(0); break;
    case 'QUARTERLY': dt.setMonth(dt.getMonth() + 3); break;
    case 'HALF_YEARLY': dt.setMonth(dt.getMonth() + 6); break;
    case 'YEARLY': dt.setMonth(dt.getMonth() + 12); break;
    default: dt.setMonth(dt.getMonth() + 1); // MONTHLY
  }
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const fmtDate = (s: string) => { const [y, m, d] = s.split('-'); return `${d}-${m}-${y}`; };

const num = (v: any) => Number(v) || 0;
const round2 = (n: number) => Math.round(n * 100) / 100;

const SectionCard: React.FC<{ title: string; sub?: string; children: React.ReactNode }> = ({ title, sub, children }) => (
  <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
    <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${P.line}` }}>
      <Typography sx={{ fontSize: '13.5px', fontWeight: 800, color: P.strong }}>{title}</Typography>
      {sub && <Typography sx={{ fontSize: '12px', color: P.muted, mt: '2px' }}>{sub}</Typography>}
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Paper>
);

const RecurringJournalWorkspacePage: React.FC = () => {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<CoaAccount[]>([]);
  const [companyName, setCompanyName] = useState('');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [tpl, setTpl] = useState<any | null>(null);
  const [tplLoading, setTplLoading] = useState(false);
  const [frequency, setFrequency] = useState('MONTHLY');
  const [startDate, setStartDate] = useState(todayISO());
  const [endMode, setEndMode] = useState<'NONE' | 'AFTER' | 'ON'>('NONE');
  const [maxOccurrences, setMaxOccurrences] = useState('12');
  const [endDate, setEndDate] = useState('');
  const [postingMode, setPostingMode] = useState('AUTO_POST');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    recurringService.templates().then((r) => setTemplates(r.templates || [])).catch(() => {});
    coaService.accounts().then((r) => setAccounts(r.accounts || [])).catch(() => {});
    settingsService.getCompanySettings().then((c: any) => setCompanyName(c?.companyName || c?.legalName || c?.name || '')).catch(() => {});
  }, []);

  // Fetch the selected template's lines (real endpoint) for the preview.
  useEffect(() => {
    if (!templateId) { setTpl(null); return; }
    setTplLoading(true);
    financeWorkspaceService.getTemplate(Number(templateId))
      .then((r: any) => setTpl(r.template || r))
      .catch(() => setTpl(null))
      .finally(() => setTplLoading(false));
  }, [templateId]);

  const acctLabel = useMemo(() => {
    const m = new Map(accounts.map((a) => [a.id, `${a.account_code} · ${a.account_name}`]));
    return (id: number) => m.get(id) || `Account #${id}`;
  }, [accounts]);

  const tplLines: any[] = tpl?.lines || [];
  const tplDr = round2(tplLines.reduce((s, l) => s + num(l.debitAmount ?? l.debit_amount), 0));
  const tplCr = round2(tplLines.reduce((s, l) => s + num(l.creditAmount ?? l.credit_amount), 0));
  const tplBalanced = tplLines.length > 0 && Math.abs(tplDr - tplCr) < 0.01;

  const upcoming = useMemo(() => {
    if (!startDate) return [] as string[];
    const cap = endMode === 'AFTER' && Number(maxOccurrences) > 0 ? Math.min(Number(maxOccurrences), 6) : 6;
    const out: string[] = [];
    let cur = startDate;
    for (let i = 0; i < cap; i += 1) {
      if (endMode === 'ON' && endDate && cur > endDate) break;
      out.push(cur);
      cur = advance(cur, frequency);
    }
    return out;
  }, [startDate, frequency, endMode, maxOccurrences, endDate]);

  const checks = [
    { ok: !!name.trim(), label: name.trim() ? 'Name set' : 'Name required' },
    { ok: !!templateId, label: templateId ? 'Template selected' : 'Select a template' },
    { ok: !!startDate, label: startDate ? `Starts ${fmtDate(startDate)}` : 'Start date required' },
    { ok: !templateId || tplBalanced || tplLoading, label: !templateId ? 'Template —' : (tplLoading ? 'Checking template…' : (tplBalanced ? 'Template balanced' : 'Template not balanced')) },
    { ok: endMode !== 'AFTER' || Number(maxOccurrences) > 0, label: endMode === 'AFTER' ? `Ends after ${maxOccurrences || 0}` : (endMode === 'ON' ? (endDate ? `Ends ${fmtDate(endDate)}` : 'Pick an end date') : 'Open-ended') },
  ];
  const canSave = !!name.trim() && !!templateId && !!startDate && !saving
    && (endMode !== 'AFTER' || Number(maxOccurrences) > 0)
    && (endMode !== 'ON' || !!endDate)
    && (!templateId || tplBalanced);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      await recurringService.create({
        name: name.trim(),
        templateId: Number(templateId),
        frequency,
        startDate,
        endDate: endMode === 'ON' && endDate ? endDate : null,
        maxOccurrences: endMode === 'AFTER' && Number(maxOccurrences) > 0 ? Number(maxOccurrences) : null,
        postingMode,
        description: description || null,
      });
      navigate('/finance/recurring-journals');
    } catch (e: any) { setError(e?.message || 'Failed to create recurring journal'); setSaving(false); }
  };

  return (
    <Box sx={{ bgcolor: P.bg, minHeight: '100%', pb: 6, color: P.text }}>
      <WorkspaceHeader
        onBack={() => navigate('/finance/recurring-journals')}
        title="New Recurring Journal"
        subtitle="Configure a reliable accounting automation — what is generated, when it runs and how it posts."
        chip={<StatusPill label={postingMode === 'AUTO_POST' ? 'Auto-post' : postingMode === 'APPROVAL' ? 'Approval' : 'Draft'} tone={postingMode === 'AUTO_POST' ? 'amber' : 'primary'} />}
        actions={(
          <>
            <Button variant="outlined" sx={outBtn} onClick={() => navigate('/finance/recurring-journals')} disabled={saving}>Cancel</Button>
            <Button variant="contained" sx={priBtn} onClick={save} disabled={!canSave}
              startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveOutlined sx={{ fontSize: 18 }} />}>
              Create
            </Button>
          </>
        )}
      />
      <ValidationStrip items={checks} />

      <Box sx={{ px: PX, pt: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) 340px' }, gap: 2.5, alignItems: 'start' }}>
        <Box sx={{ minWidth: 0 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          <SectionCard title="Recurring Journal">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0,1fr))' }, gap: '16px' }}>
              <Box><FieldLabel text="Name" required /><TextField fullWidth size="small" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Monthly Office Rent" sx={fieldSx} /></Box>
              <Box><FieldLabel text="Company / Legal Entity" /><TextField fullWidth size="small" value={companyName || 'This workspace'} disabled sx={fieldSx} helperText="One entity per workspace" /></Box>
              <Box><FieldLabel text="Journal Template" required /><TextField select fullWidth size="small" value={templateId} onChange={(e) => setTemplateId(e.target.value)} sx={fieldSx} helperText={templates.length ? 'Balanced template these journals are cloned from' : 'No templates yet'}>
                <MenuItem value="">— Select a template —</MenuItem>
                {templates.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}{t.category ? ` · ${t.category}` : ''}</MenuItem>)}
              </TextField></Box>
              <Box><FieldLabel text="Currency" /><TextField fullWidth size="small" value="INR" disabled sx={fieldSx} /></Box>
              <Box sx={{ gridColumn: '1 / -1' }}><FieldLabel text="Description" /><TextField fullWidth size="small" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional explanation" sx={fieldSx} /></Box>
            </Box>
          </SectionCard>

          <SectionCard title="Schedule">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0,1fr))' }, gap: '16px', mb: 1 }}>
              <Box><FieldLabel text="Frequency" required /><TextField select fullWidth size="small" value={frequency} onChange={(e) => setFrequency(e.target.value)} sx={fieldSx}>
                {FREQUENCIES.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
              </TextField></Box>
              <Box><FieldLabel text="Start Date" required /><TextField type="date" fullWidth size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={fieldSx} /></Box>
            </Box>
            <FieldLabel text="Ends" />
            <RadioGroup value={endMode} onChange={(e) => setEndMode(e.target.value as any)}>
              <FormControlLabel value="NONE" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13.5 }}>No end date (open-ended)</Typography>} />
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <FormControlLabel value="AFTER" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13.5 }}>End after</Typography>} sx={{ mr: 0 }} />
                <TextField type="number" size="small" value={maxOccurrences} onChange={(e) => setMaxOccurrences(e.target.value)} disabled={endMode !== 'AFTER'} sx={{ ...fieldSx, width: 90 }} />
                <Typography sx={{ fontSize: 13.5, color: P.muted }}>occurrences</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <FormControlLabel value="ON" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13.5 }}>End on</Typography>} sx={{ mr: 0 }} />
                <TextField type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={endMode !== 'ON'} sx={{ ...fieldSx, width: 180 }} />
              </Stack>
            </RadioGroup>
          </SectionCard>

          <SectionCard title="Execution">
            <FieldLabel text="Posting Mode" />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ mb: 1 }}>
              {POSTING_MODES.map((m) => {
                const active = postingMode === m.value;
                return (
                  <Box key={m.value} onClick={() => setPostingMode(m.value)} sx={{
                    flex: 1, p: 1.5, borderRadius: '10px', cursor: 'pointer',
                    border: `1.5px solid ${active ? P.primary : P.border}`, bgcolor: active ? P.primarySoft : '#fff',
                  }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: active ? P.primary : P.strong }}>{m.label}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: P.muted, mt: '2px' }}>{m.hint}</Typography>
                  </Box>
                );
              })}
            </Stack>
            {postingMode === 'AUTO_POST' && (
              <Alert severity="warning" icon={<WarningAmber />} sx={{ borderRadius: 2, py: 0.25 }}>
                Auto-post affects the general ledger without manual review.
              </Alert>
            )}
          </SectionCard>
        </Box>

        {/* Right rail: template preview + upcoming */}
        <Box sx={{ position: { lg: 'sticky' }, top: { lg: 16 } }}>
          <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${P.line}` }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: P.muted }}>Template Preview</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              {!templateId ? (
                <Typography sx={{ fontSize: 12.5, color: P.ph }}>Select a template to preview the journal that will be generated.</Typography>
              ) : tplLoading ? (
                <Box sx={{ display: 'grid', placeItems: 'center', py: 3 }}><CircularProgress size={20} /></Box>
              ) : (
                <>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: P.strong }}>{tpl?.name}</Typography>
                  <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, mb: 1.25 }}>
                    <Chip size="small" label={`${tplLines.length} lines`} sx={{ bgcolor: '#f1f5f9', color: P.muted, fontWeight: 700 }} />
                    <Chip size="small" icon={tplBalanced ? <CheckCircle sx={{ fontSize: '15px !important' }} /> : <WarningAmber sx={{ fontSize: '15px !important' }} />}
                      label={tplBalanced ? 'Balanced' : 'Unbalanced'}
                      sx={{ bgcolor: tplBalanced ? P.greenBg : P.amberBg, color: tplBalanced ? P.green : P.amber, fontWeight: 700 }} />
                  </Stack>
                  {tplLines.map((l, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: '5px', borderTop: `1px solid ${P.tline}` }}>
                      <Typography sx={{ fontSize: 12, color: P.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acctLabel(l.accountId ?? l.account_id)}</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: num(l.debitAmount ?? l.debit_amount) ? P.strong : P.green, whiteSpace: 'nowrap' }}>
                        {num(l.debitAmount ?? l.debit_amount) ? `${formatIndianNumber(num(l.debitAmount ?? l.debit_amount))}` : `(${formatIndianNumber(num(l.creditAmount ?? l.credit_amount))})`}
                      </Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1, borderColor: P.line }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 12, color: P.muted }}>Debit / Credit</Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: P.strong }}>{formatIndianNumber(tplDr)} / {formatIndianNumber(tplCr)}</Typography>
                  </Box>
                </>
              )}
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ ...cardSx }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${P.line}`, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleOutlined sx={{ fontSize: 18, color: P.primary }} />
              <Typography sx={{ fontSize: '12px', fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: P.muted }}>Upcoming Occurrences</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              {upcoming.length === 0 ? (
                <Typography sx={{ fontSize: 12.5, color: P.ph }}>Set a start date to preview runs.</Typography>
              ) : upcoming.map((d, i) => (
                <Box key={d} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: '6px', borderTop: i ? `1px solid ${P.tline}` : 'none' }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: i === 0 ? 800 : 600, color: i === 0 ? P.primary : P.text }}>{fmtDate(d)}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: P.muted }}>{i === 0 ? 'first run' : `run ${i + 1}`}</Typography>
                </Box>
              ))}
              {endMode === 'NONE' && <Typography sx={{ fontSize: 11, color: P.ph, mt: 1 }}>…continues open-ended</Typography>}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default RecurringJournalWorkspacePage;
